import React, { Suspense, lazy, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useI18n } from "@/context/I18nContext";
import { useAuth } from "@/context/AuthContext";
import PublicHeader from "@/components/layout/PublicHeader";
import { ArrowRight, Zap, Mail, Target, Shield, Sparkles, Briefcase, X, Globe } from "lucide-react";
import { useSEO } from "@/lib/useSEO";

// Spline loads ~1MB at runtime — keep it lazy so it never blocks page paint.
const Spline = lazy(() => import("@splinetool/react-spline"));

const CANDIDATE_SCENE = "https://prod.spline.design/0BJfwkj3OFykm-fj/scene.splinecode";
const EMPLOYER_SCENE  = "https://prod.spline.design/TIuZ9w6wYQER7IcQ/scene.splinecode";

// Country pool for the Hire-side search picker.
const HIRE_COUNTRIES = [
  { code: "sa", name: "Saudi Arabia", flag: "🇸🇦" },
  { code: "ae", name: "United Arab Emirates", flag: "🇦🇪" },
  { code: "qa", name: "Qatar", flag: "🇶🇦" },
  { code: "uk", name: "United Kingdom", flag: "🇬🇧" },
  { code: "de", name: "Germany", flag: "🇩🇪" },
  { code: "fr", name: "France", flag: "🇫🇷" },
  { code: "in", name: "India", flag: "🇮🇳" },
  { code: "ph", name: "Philippines", flag: "🇵🇭" },
  { code: "ca", name: "Canada", flag: "🇨🇦" },
];

// A user has employer access if they're a recruiter, OR have an explicit
// employer override (test accounts), OR have the Bundle / vertical_pro_override.
function _hasEmployerAccess(u) {
  if (!u) return false;
  if (u.role === "recruiter") return true;
  if (u.employer_pro_override === true) return true;
  if (u.vertical_pro_override === true) return true;
  const sub = (u.subscription || "").toLowerCase();
  if (sub === "employer_pro" || sub === "recruiter") return true;
  return false;
}

const partners = [
  "Aramco", "STC Pay", "NEOM", "SABIC", "Noon", "Jahez",
  "Al Rajhi Bank", "PIF", "Mozn", "Tamara", "Almarai", "Jarir",
];

const features = [
  {
    icon: Sparkles,
    title: "A search engine that reads every posting",
    body:
      "Our matching engine scores every posting against your profile and drafts a tailored cover letter per application — no generic spray-and-pray.",
    testid: "feature-ai",
  },
  {
    icon: Zap,
    title: "100 applications. One click.",
    body:
      "Pick a role, pick your cities in the Kingdom, hit Apply. Maqam queues and submits to every employer on the list simultaneously.",
    testid: "feature-bulk",
  },
  {
    icon: Mail,
    title: "Private platform inbox",
    body:
      "You get username@maqam-ksa.com on sign-up. Every employer reply arrives in your Maqam inbox — your real email address stays hidden.",
    testid: "feature-inbox",
  },
  {
    icon: Target,
    title: "Tuned for Saudi Arabia",
    body:
      "Riyadh, Jeddah, Dammam, NEOM, and beyond. A curated Kingdom-first job graph across healthcare, engineering, tech, finance, and more.",
    testid: "feature-market",
  },
];

export default function Landing() {
  const { t } = useI18n();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [ctasHidden, setCtasHidden] = React.useState(false);
  const [mode, setMode] = useState("candidate");        // "candidate" | "employer"
  const [showCountryPicker, setShowCountryPicker] = useState(false);
  const [selectedCountries, setSelectedCountries] = useState([]);

  useSEO({
    title: "Maqam — Apply to 100 Saudi jobs in one click",
    description: "Maqam is Saudi Arabia's AI-powered job-application platform. Upload your CV once, discover live Gulf jobs, and bulk-apply from your own Gmail. Built in the Kingdom.",
    path: "/",
  });

  const isEmployer = mode === "employer";

  const handleFindWorkers = () => {
    // Auth gate first — unauth users get sent to the employer landing.
    if (!user) {
      navigate("/hire");
      return;
    }
    // Paywall gate — without employer access, send to Hire landing for pricing.
    if (!_hasEmployerAccess(user)) {
      navigate("/hire#pricing");
      return;
    }
    // Pro employer → open the country picker.
    setShowCountryPicker(true);
  };

  const toggleCountry = (code) => {
    setSelectedCountries((prev) =>
      prev.includes(code) ? prev.filter((c) => c !== code) : [...prev, code]
    );
  };

  const startEmployerSearch = () => {
    setShowCountryPicker(false);
    // Carry the selected countries into the recruiter dashboard via querystring.
    const qs = selectedCountries.length ? `?countries=${selectedCountries.join(",")}` : "";
    navigate(`/recruiter/search${qs}`);
  };

  return (
    <div className="min-h-screen bg-[#1a1a1c] text-white">
      <PublicHeader />

      {/* Hero — centered over Spline scene, click-to-hide CTAs */}
      <section className="relative overflow-hidden bg-[#1a1a1c]" style={{ minHeight: "92vh" }}>
        {/* Full-bleed centered 3D scene (click to toggle CTA visibility) */}
        <div
          className="absolute inset-0 z-0 cursor-pointer"
          onClick={() => setCtasHidden((v) => !v)}
          data-testid="hero-scene-toggle"
        >
          <Suspense fallback={<div className="absolute inset-0 bg-[#1a1a1c]" />}>
            <Spline
              key={mode}  // force a remount when mode flips so the new scene loads cleanly
              scene={isEmployer ? EMPLOYER_SCENE : CANDIDATE_SCENE}
              style={{ width: "100%", height: "100%" }}
            />
          </Suspense>
          {/* Soft radial so text is readable */}
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,_transparent_35%,_rgba(7,7,9,0.75)_100%)] pointer-events-none" />
        </div>

        <div className="relative z-10 max-w-[1200px] mx-auto px-6 md:px-10 pt-28 md:pt-32 pb-20 md:pb-24 flex flex-col items-center text-center pointer-events-none fade-up">
          <div className="inline-flex items-center gap-3 mb-8 font-sans text-[11px] uppercase tracking-[0.3em] text-white/80 backdrop-blur-xl bg-white/[0.04] border border-white/15 px-4 py-2 rounded-full pointer-events-auto">
            <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse" />
            {isEmployer ? "For employers · KSA talent pool" : "Vision 2030 Aligned"}
          </div>

          <h1
            className={`font-display font-normal text-6xl sm:text-7xl md:text-[7rem] tracking-[-0.02em] leading-[0.92] mb-8 max-w-5xl text-white drop-shadow-[0_0_30px_rgba(0,0,0,0.85)] transition-opacity duration-500 ${ctasHidden ? "opacity-0" : "opacity-100"}`}
            style={{ fontVariationSettings: "'opsz' 144, 'SOFT' 0" }}
            data-testid="hero-title"
          >
            {isEmployer ? (
              <>
                Find your top
                <br />
                <span
                  className="font-display italic font-light text-white"
                  style={{ fontVariationSettings: "'opsz' 144, 'SOFT' 100" }}
                >
                  workers.
                </span>
              </>
            ) : (
              <>
                {t("tagline").split(".")[0]}.
                <br />
                <span
                  className="font-display italic font-light text-white"
                  style={{ fontVariationSettings: "'opsz' 144, 'SOFT' 100" }}
                >
                  {t("tagline").split(".")[1]?.trim()}.
                </span>
              </>
            )}
          </h1>

          <p className={`text-lg md:text-xl text-white max-w-2xl leading-relaxed mb-12 font-sans font-medium tracking-wide transition-opacity duration-500 ${ctasHidden ? "opacity-0" : "opacity-100"}`}>
            {isEmployer
              ? "Search the world's top talent — scored, verified, ready to be approached. $700/month, flat."
              : t("hero_sub")}
          </p>

          {/* CTAs: hidden when user clicks the scene */}
          <div
            className={`flex flex-col sm:flex-row items-center gap-4 w-full sm:w-auto pointer-events-auto transition-all duration-500 ${
              ctasHidden ? "opacity-0 translate-y-2 pointer-events-none" : "opacity-100 translate-y-0"
            }`}
            data-testid="hero-ctas"
          >
            {isEmployer ? (
              <>
                <button
                  onClick={handleFindWorkers}
                  className="group bg-white text-black hover:bg-white/90 font-sans font-semibold text-base px-8 py-4 transition-all duration-300 flex items-center justify-center gap-3 rounded-full w-full sm:w-auto"
                  data-testid="hero-find-workers"
                >
                  <Globe className="w-4 h-4" />
                  Find your top workers
                  <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </button>
                <button
                  onClick={() => setMode("candidate")}
                  className="backdrop-blur-xl bg-white/[0.05] border border-white/15 text-white hover:bg-white/10 font-sans font-medium text-base px-8 py-4 rounded-full transition-all duration-300 w-full sm:w-auto flex items-center justify-center gap-2"
                  data-testid="hero-switch-to-candidate"
                >
                  ← Switch to candidate
                </button>
              </>
            ) : (
              <>
                <Link
                  to="/register"
                  className="group bg-white text-black hover:bg-white/90 font-sans font-semibold text-base px-8 py-4 transition-all duration-300 flex items-center justify-center gap-3 rounded-full w-full sm:w-auto"
                  data-testid="hero-get-started"
                >
                  {t("get_started")}
                  <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </Link>
                <button
                  onClick={() => setMode("employer")}
                  className="backdrop-blur-xl bg-white/[0.05] border border-white/15 text-white hover:bg-white/10 font-sans font-medium text-base px-8 py-4 rounded-full transition-all duration-300 w-full sm:w-auto flex items-center justify-center gap-2"
                  data-testid="hero-switch-to-employer"
                >
                  <Briefcase className="w-4 h-4" />
                  Switch to employer
                </button>
              </>
            )}
          </div>

          <div className={`mt-auto pt-10 w-full max-w-2xl grid grid-cols-3 gap-6 text-center absolute bottom-10 left-1/2 -translate-x-1/2 transition-opacity duration-500 ${ctasHidden ? "opacity-0" : "opacity-100"}`}>
            {isEmployer ? (
              <>
                <div>
                  <div className="font-display text-2xl md:text-3xl text-white mb-1">$700</div>
                  <div className="font-sans text-[10px] uppercase tracking-widest text-white/80 font-bold">/ month flat</div>
                </div>
                <div>
                  <div className="font-display text-2xl md:text-3xl text-white mb-1">9</div>
                  <div className="font-sans text-[10px] uppercase tracking-widest text-white/80 font-bold">Countries</div>
                </div>
                <div>
                  <div className="font-display text-2xl md:text-3xl text-white mb-1">0-100</div>
                  <div className="font-sans text-[10px] uppercase tracking-widest text-white/80 font-bold">Maqam score</div>
                </div>
              </>
            ) : (
              <>
                <div>
                  <div className="font-display text-2xl md:text-3xl text-white mb-1">100+</div>
                  <div className="font-sans text-[10px] uppercase tracking-widest text-white/80 font-bold">Apps per click</div>
                </div>
                <div>
                  <div className="font-display text-2xl md:text-3xl text-white mb-1">50h</div>
                  <div className="font-sans text-[10px] uppercase tracking-widest text-white/80 font-bold">Time saved</div>
                </div>
                <div>
                  <div className="font-display text-2xl md:text-3xl text-white mb-1">13</div>
                  <div className="font-sans text-[10px] uppercase tracking-widest text-white/80 font-bold">KSA cities</div>
                </div>
              </>
            )}
          </div>
        </div>
      </section>

      {/* Country picker modal — employer Pro only */}
      {showCountryPicker && (
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center bg-black/85 backdrop-blur-sm p-4"
          onClick={() => setShowCountryPicker(false)}
          data-testid="country-picker-modal"
        >
          <div
            className="bg-[#141416] border-2 border-white max-w-2xl w-full max-h-[85vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between p-8 border-b border-white/10">
              <div>
                <div className="font-mono text-[10px] uppercase tracking-widest text-white/50 mb-2">
                  / Source talent from
                </div>
                <h2 className="font-display font-black text-3xl tracking-tighter">
                  Pick the countries you want to hire from
                </h2>
                <p className="text-sm text-white/60 mt-2">
                  Select one or more. We'll surface candidates in those regions, ranked by Maqam Score.
                </p>
              </div>
              <button
                onClick={() => setShowCountryPicker(false)}
                className="p-2 hover:bg-white/10 transition-colors"
                aria-label="Close"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-8 grid grid-cols-2 sm:grid-cols-3 gap-3">
              {HIRE_COUNTRIES.map((c) => {
                const active = selectedCountries.includes(c.code);
                return (
                  <button
                    key={c.code}
                    onClick={() => toggleCountry(c.code)}
                    className={`p-4 border text-left transition-colors flex items-center gap-3 ${
                      active
                        ? "bg-white text-black border-white"
                        : "border-white/15 hover:border-white/40 hover:bg-white/5"
                    }`}
                    data-testid={`country-${c.code}`}
                  >
                    <span className="text-3xl leading-none">{c.flag}</span>
                    <span className="font-display font-bold text-sm leading-tight">{c.name}</span>
                  </button>
                );
              })}
            </div>

            <div className="p-8 border-t border-white/10 flex items-center justify-between gap-4">
              <div className="text-xs font-mono uppercase tracking-widest text-white/50">
                {selectedCountries.length === 0
                  ? "No countries — searches all 9"
                  : `${selectedCountries.length} ${selectedCountries.length === 1 ? "country" : "countries"} selected`}
              </div>
              <button
                onClick={startEmployerSearch}
                className="bg-white text-black hover:bg-white/85 font-bold px-8 py-3 transition-colors flex items-center gap-2"
                data-testid="country-picker-search"
              >
                Open candidate search <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Marquee */}
      <section className="overflow-hidden bg-[#1a1a1c] py-8">
        <div className="flex gap-20 whitespace-nowrap marquee-track">
          {[...partners, ...partners, ...partners].map((p, i) => (
            <span key={i} className="font-display font-normal italic text-2xl text-white/70 tracking-tight">
              {p}
            </span>
          ))}
        </div>
      </section>

      {/* Features */}
      <section id="features" className="relative overflow-hidden border-b border-white/10 bg-[#1a1a1c]">
        {/* Ambient 3D-style backdrop: rotating wireframe globe + drifting particles */}
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 pointer-events-none" aria-hidden="true">
          <div className="wire-globe" />
        </div>
        <div className="particles-field" aria-hidden="true">
          <span /><span /><span /><span /><span />
          <span /><span /><span /><span /><span />
        </div>

        <div className="relative z-10 max-w-[1400px] mx-auto px-6 md:px-10 py-24 md:py-40">
          <div className="grid md:grid-cols-12 gap-16 mb-24">
            <div className="md:col-span-6">
              <div className="font-sans text-xs uppercase tracking-[0.2em] text-white/60 mb-6 flex items-center gap-3">
                <span className="w-8 h-px bg-white/30" /> Features
              </div>
              <h2 className="font-display font-normal text-4xl md:text-6xl tracking-tight leading-[1.05] text-white">
                Engineered for the Kingdom's elite.
              </h2>
            </div>
            <div className="md:col-span-5 md:col-start-8 text-white/60 text-lg md:text-xl font-light leading-relaxed self-end">
              Stop refreshing career pages. Stop copy-pasting the same cover letter. Maqam's search engine reads each posting,
              matches it to your profile, drafts the letter, and submits — in parallel, across the whole Kingdom.
            </div>
          </div>
          <div className="grid md:grid-cols-2 gap-8 md:gap-12">
            {features.map((f) => (
              <div key={f.title} className="group bg-white/[0.02] border border-white/10 p-12 hover:border-white/40 transition-all duration-500 hover:-translate-y-2 rounded-2xl" data-testid={f.testid}>
                <div className="w-16 h-16 rounded-full border border-white/15 bg-white/[0.02] flex items-center justify-center mb-8 group-hover:bg-white group-hover:border-white transition-colors">
                  <f.icon className="w-7 h-7 text-white group-hover:text-[#1a1a1c] transition-colors stroke-[1.5]" />
                </div>
                <h3 className="font-display font-normal text-3xl tracking-tight mb-5 text-white">{f.title}</h3>
                <p className="text-white/60 leading-relaxed font-light text-lg">{f.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-[#1a1a1c] text-white relative overflow-hidden border-y border-white/10">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-32 md:py-40 flex flex-col md:flex-row gap-16 items-start md:items-center justify-between relative z-10">
          <div className="max-w-2xl">
            <div className="font-sans text-xs uppercase tracking-[0.2em] text-white/60 mb-6 flex items-center gap-3">
              <span className="w-8 h-px bg-white/30" /> Privacy First
            </div>
            <h2 className="font-display font-normal text-5xl md:text-7xl tracking-tight leading-[1] mb-8 text-white">
              Your data, your consent, <span className="italic">your control.</span>
            </h2>
            <p className="text-white/60 text-xl font-light max-w-xl leading-relaxed">
              Every bulk send requires explicit consent. You can revoke access and delete your data anytime. PDPL-aligned.
            </p>
          </div>
          <Link
            to="/register"
            className="border border-white/30 text-white hover:bg-white hover:text-[#1a1a1c] font-sans font-medium text-lg px-10 py-5 flex items-center justify-center gap-3 transition-all duration-300 rounded-full w-full md:w-auto"
            data-testid="cta-register"
          >
            Create free account <ArrowRight className="w-5 h-5" />
          </Link>
        </div>
      </section>

      <footer className="border-t border-white/10 bg-[#1a1a1c] text-white/50 text-sm font-sans">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-12 flex flex-col md:flex-row gap-8 justify-between items-center">
          <div className="flex flex-col md:flex-row items-center gap-3 md:gap-6">
            <div className="flex items-center gap-3">
              <div className="w-6 h-6 bg-white text-[#1a1a1c] flex items-center justify-center font-display font-black text-xs">M</div>
              <span>© 2026 Maqam™. All rights reserved.</span>
            </div>
            <span className="hidden md:inline text-white/30">·</span>
            <span className="text-xs text-white/40">Built in the Kingdom of Saudi Arabia</span>
          </div>
          <div className="flex gap-8 uppercase tracking-widest text-xs font-medium">
            <Link to="/privacy-policy" className="hover:text-white transition-colors" data-testid="footer-privacy">Privacy</Link>
            <Link to="/terms" className="hover:text-white transition-colors" data-testid="footer-terms">Terms</Link>
            <Link to="/copyright" className="hover:text-white transition-colors" data-testid="footer-copyright">Copyright</Link>
            <a href="mailto:support@maqam-ksa.com" className="hover:text-white transition-colors" data-testid="footer-contact">Contact</a>
          </div>
        </div>
      </footer>
    </div>
  );
}
